import { useCallback, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import * as actions from '@actions';
import { useDebouncedCallback } from '@mantine/hooks';
import { useSessionAppointment } from './useSessionAppointment';
import { useSessionFormState } from './useSessionFormState';
import { useSessionOutcome } from './useSessionOutcome';
import { useSessionAppointmentActions } from './useSessionAppointmentActions';
import useAppointmentDetails from './appointments/useAppointmentDetails';
import { useValidateActionTab } from './useValidateActionTab';
import useAsyncAwareDebounceCallback from 'vl-common/src/hooks/useAsyncAwareDebounceCallback';
import ClinicianSessionFormState from '@components/Clinicians/Session/hooks/clinicianSessionFormState';

type SessionOutcomeMutationParams = Omit<
  Parameters<typeof actions.appointmentOutcomeSave>[0],
  'user_guid' | 'appt_id'
> & { continueOnValidationError?: boolean };

type MutationResult = {
  type?: string;
  error?: { code?: string; message?: string };
  payload?: unknown;
};

export const useSessionOutcomeMutation = () => {
  const dispatch = useDispatch();
  const { appointment, refetchAppointment } = useSessionAppointment();
  const { appointment_id, audit_appt_id, appointment_completed, clinician_user_guid } = appointment;
  const [, setIsActionsSaving] = useSessionFormState('isActionsSaving', 0);
  const { outcome, setOutcome } = useSessionOutcome();

  const [rtt, setRTT] = useSessionFormState('Referral To Treatment');
  const [priority, setPriority] = useSessionFormState('Priority');

  const appointmentActions = useSessionAppointmentActions();
  const { refetch: refetchAppointmentDetails } = useAppointmentDetails(audit_appt_id || appointment_id);

  const validateActions = useValidateActionTab();

  const incrementQueue = useCallback(() => setIsActionsSaving((prev) => (prev ? prev + 1 : 1)), [setIsActionsSaving]);

  const decrementQueue = useCallback(() => setIsActionsSaving((prev) => (prev ? prev - 1 : 0)), [setIsActionsSaving]);

  const debouncedRefetch = useDebouncedCallback(async () => await refetchAppointmentDetails(), 1500);

  const { mutateAsync, mutate } = useMutation({
    mutationFn: ({
      continueOnValidationError = false,
      ...request
    }: SessionOutcomeMutationParams): Promise<MutationResult | null> => {
      if (appointment_completed) return Promise.resolve(null);

      const { isValid } = validateActions();
      if (!isValid && !continueOnValidationError) return Promise.resolve(null);

      const body = {
        user_guid: clinician_user_guid,
        appt_id: appointment_id,
        rtt,
        priority,
        outcome_code: outcome,
        appt_actions: appointmentActions,
        ...request
      };

      const lastSavedPayloadKey = `appointment[${appointment_id}].last_saved_outcome_payload`;
      const lastSavedPayload = ClinicianSessionFormState.get<string>(lastSavedPayloadKey);
      const payload = JSON.stringify(body);
      if (lastSavedPayload === payload) return Promise.resolve(null);

      ClinicianSessionFormState.set(lastSavedPayloadKey, payload);

      return dispatch(actions.appointmentOutcomeSave(body));
    },
    onSuccess: (data, request) => {
      if (!data) return;

      debouncedRefetch();

      if (!request.outcome_code || !request.priority) return;

      if (request.outcome_code !== outcome) {
        setOutcome(request.outcome_code);
      }

      if (request.priority !== priority) {
        setPriority(request.priority);
      }

      if (request.rtt !== rtt && request.rtt) {
        setRTT(request.rtt);
      }
    },
    onSettled: (data) => {
      if (data) refetchAppointment();
    }
  });

  const asyncAwareDebounceMutate = useCallback(
    (...args: Parameters<typeof mutate>) => mutateAsync(...args),
    [mutateAsync, mutate]
  );

  const { debouncedFunction: debouncedMutate, hasUnsavedChanges } = useAsyncAwareDebounceCallback(
    async (...args: Parameters<typeof mutate>) => asyncAwareDebounceMutate(...args),
    1000
  );

  useEffect(() => {
    if (hasUnsavedChanges) {
      incrementQueue();
    }
    return () => decrementQueue();
  }, [hasUnsavedChanges, incrementQueue, decrementQueue]);

  return {
    mutateAsync,
    mutate,
    debouncedMutate
  };
};
